import React from 'react'
import PropTypes from 'prop-types'
import { Field, FieldArray } from 'formik'
import { get } from 'lodash'
import { Trans, useTranslation } from 'react-i18next'
import { useSortable } from '@dnd-kit/react/sortable'
import { DotsSixVertical } from '@phosphor-icons/react'

import { ConfirmationPopup } from '@/components/ui/confirmation-popup'
import { Divider } from '@/components/ui/divider'
import { Typography } from '@/components/ui/typography'
import { popupFactory, toastFactory } from '@/factories'

import Accordion from '../../Accordion/Accordion'
import CollectionItem from '../../Collection/CollectionItem'
import withAccordionControl from '../../Accordion/withAccordionControl'
import TextField from '../../FormLegacy/FormFieldsV2/TextField'
import SelectField from '../../FormLegacy/FormFieldsV2/SelectField'
import ToggleField from '../../FormLegacy/FormFieldsV2/ToggleField'
import Icon from '../../Icons'
import OrderArrows from '../../OrderArrows/OrderArrows'

import { switchOrderForItems } from './OrganisationBooking'

const ServiceQuestionItem = ({
  index: questionIdx,
  question,
  bookingType,
  deleteServiceQuestion,
  deleteAnswer,
  accordionCollapsed,
  accordionToggle,
  questionArrayHelpers,
  reorderQuestion,
}) => {
  const { t } = useTranslation(['organisation', 'common'])
  const isNewQuestion = !('id' in question)
  const isQuestionOpen = isNewQuestion || !accordionCollapsed
  // Listens on the single, page-level DragDropProvider — see the matching comment in
  // ServiceBookingTypeItem.jsx's useDragDropMonitor call for why. `group` scopes reordering to
  // this question's own booking type so it can't be conflated with another one's questions.
  const { ref: dragRef, handleRef } = useSortable({
    id: question?.id ?? question?.clientId ?? `question-${questionIdx}`,
    index: questionIdx,
    disabled: isQuestionOpen,
    type: 'question',
    // See the matching comment on the category-level useSortable call in
    // OrganisationBooking.jsx: these lists are strictly independent, nothing crosses layers.
    accept: 'question',
    group: String(bookingType?.id),
  })
  const reorderQuestions = (questions, direction, setFieldValue) => {
    const updates = switchOrderForItems(questions, questionIdx, direction)
    if (updates.length !== 2) return

    const targetIndex = questionIdx + direction

    // update new order values
    setFieldValue(`questions.${questionIdx}.order`, updates[0].order)
    setFieldValue(`questions.${targetIndex}.order`, updates[1].order)

    // swap element indexes in FieldArray questions
    questionArrayHelpers.swap(questionIdx, targetIndex)

    // Persist immediately for already-saved questions, matching category/booking-type
    // reorder behavior. Unsaved questions have nothing to PATCH yet — their new order is
    // captured above via setFieldValue and sent on the form's own Save.
    updates.forEach((q) => {
      if (typeof q.id === 'number') {
        reorderQuestion(q.id, q.order)?.catch?.((err) => console.warn(err))
      }
    })
  }
  return (
    <Field name={`questions.${questionIdx}`}>
      {({
        field: { value },
        form: { values, errors, touched, handleChange, setFieldTouched, setFieldValue },
      }) => {
        const answersType = value.answersType || (value.answers[0] && value.answers[0].type)
        return (
          <Accordion
            rootRef={dragRef}
            collapsed={isNewQuestion ? false : accordionCollapsed}
            additionalClass={`ctp-shop-item${isQuestionOpen ? ' ctp-shop-item--open' : ''}`}
          >
            <Accordion.Head handleClick={isNewQuestion ? false : accordionToggle}>
              <CollectionItem padding={false} hover={false} border={false}>
                <span
                  ref={handleRef}
                  className={`ctp-shop-item__drag-handle${isQuestionOpen ? ' ctp-shop-item__drag-handle--disabled' : ''}`}
                >
                  <DotsSixVertical size={20} />
                </span>
                <span className="ctp-shop-item__name ctp-shop-item__name--wrap">
                  {questionIdx + 1}. {value.title}
                </span>
                <ToggleField
                  label={t('organisation:booking.question.required')}
                  name={`questions.${questionIdx}.isRequired`}
                  value={value.isRequired}
                  onChange={handleChange}
                  id={`${value.id}-questions.${questionIdx}.isRequired`}
                />
                <OrderArrows
                  upClick={() => {
                    reorderQuestions(values.questions, -1, setFieldValue)
                  }}
                  downClick={() => {
                    reorderQuestions(values.questions, 1, setFieldValue)
                  }}
                />
                <button
                  className={'button button-danger is-small has-icon ctp-shop-item__delete'}
                  onClick={() => {
                    popupFactory.open(ConfirmationPopup, {
                      cancelText: t('common:actions.cancel'),
                      confirmText: t('common:actions.delete'),
                      onConfirm: deleteServiceQuestion,
                      title: t('organisation:booking.question.deleteConfirm.title'),
                      variant: 'danger',
                      children: (
                        <Typography>
                          <Trans
                            components={[<strong />]}
                            i18nKey="organisation:booking.question.deleteConfirm.body"
                            values={{ title: value.title }}
                          />
                        </Typography>
                      ),
                    })
                  }}
                >
                  <Icon icon={'trash'} />
                </button>
              </CollectionItem>
            </Accordion.Head>
            <Accordion.Body>
              <div
                className={'form ctp-service-questions__question'}
                style={{ background: 'transparent' }}
              >
                <div className={'columns'}>
                  <div className={'column is-12'}>
                    <TextField
                      type={'text'}
                      name={`questions.${questionIdx}.title`}
                      placeholder={t('organisation:booking.question.title.questionPlaceholder')}
                      value={value.title}
                      onChange={handleChange}
                      isError={
                        get(touched, `questions.${questionIdx}.title`) &&
                        !!get(errors, `questions.${questionIdx}.title`)
                      }
                      onBlur={() => {
                        setFieldTouched(`questions.${questionIdx}.title`)
                      }}
                    />
                  </div>
                </div>
                <div className={'columns'}>
                  <div className={'column is-6'}>
                    <SelectField
                      floatDropdown
                      name={`questions.${questionIdx}.answersType`}
                      label={t('organisation:booking.question.answerType.label')}
                      value={answersType}
                      options={[
                        {
                          value: 'button',
                          label: t('organisation:booking.question.answerType.options.button'),
                        },
                        {
                          value: 'input',
                          label: t('organisation:booking.question.answerType.options.input'),
                        },
                        {
                          value: 'image',
                          label: t('organisation:booking.question.answerType.options.image'),
                        },
                        {
                          value: 'time picker',
                          label: t('organisation:booking.question.answerType.options.timePicker'),
                        },
                      ]}
                      onChange={handleChange}
                    />
                  </div>
                  <div className={'column is-6'}>
                    <SelectField
                      floatDropdown
                      name={`questions.${questionIdx}.selectType`}
                      label={t('organisation:booking.question.selectType.label')}
                      value={value.selectType}
                      options={[
                        {
                          value: 'single',
                          label: t('organisation:booking.question.selectType.single'),
                        },
                        {
                          value: 'multiple',
                          label: t('organisation:booking.question.selectType.multiple'),
                        },
                      ]}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <TextField
                  type={'text'}
                  name={`questions.${questionIdx}.modal`}
                  placeholder={t('organisation:booking.question.additionalInformation.placeholder')}
                  value={value.modal}
                  onChange={handleChange}
                />

                <FieldArray name={`questions.${questionIdx}.answers`}>
                  {(arrayHelpers) => {
                    return (
                      <React.Fragment>
                        {value.answers.map((answer, answerIdx) => {
                          const { title, pricingType, pricingValue } = answer
                          if (answersType === 'button') {
                            return (
                              <div key={answerIdx}>
                                <Divider className="ctp-shop-item__answer-divider" />
                                <div className="ctp-shop-item__answer-header">
                                  <strong className="ctp-shop-item__answer-number">
                                    {t('organisation:booking.question.answerNumber', {
                                      number: answerIdx + 1,
                                    })}
                                  </strong>
                                  <button
                                    className={
                                      'button button-danger is-small has-icon ctp-shop-item__delete'
                                    }
                                    onClick={() => {
                                      popupFactory.open(ConfirmationPopup, {
                                        cancelText: t('common:actions.cancel'),
                                        confirmText: t('common:actions.delete'),
                                        onConfirm: () => {
                                          // See the matching comment on deleteServiceQuestion in
                                          // ServiceBookingTypeItem.jsx: a purely local removal
                                          // gets wiped by Formik's enableReinitialize once the
                                          // Redux-sourced `questions` prop next re-renders.
                                          if (!('id' in answer)) {
                                            arrayHelpers.remove(answerIdx)
                                            return
                                          }
                                          return deleteAnswer(question.id, answer.id)
                                            .then(() => {
                                              arrayHelpers.remove(answerIdx)
                                              toastFactory.success(
                                                t('organisation:booking.type.toasts.answerDeleted'),
                                              )
                                            })
                                            .catch(() => {
                                              toastFactory.error(
                                                t('organisation:booking.type.errors.saveFailed'),
                                              )
                                            })
                                        },
                                        title: t(
                                          'organisation:booking.question.answerDeleteConfirm.title',
                                        ),
                                        variant: 'danger',
                                        children: (
                                          <Typography>
                                            <Trans
                                              components={[<strong />]}
                                              i18nKey="organisation:booking.question.answerDeleteConfirm.body"
                                              values={{ title }}
                                            />
                                          </Typography>
                                        ),
                                      })
                                    }}
                                  >
                                    <Icon icon={'trash'} />
                                  </button>
                                </div>
                                <TextField
                                  type={'text'}
                                  name={`questions.${questionIdx}.answers.${answerIdx}.title`}
                                  label={t('organisation:booking.question.title.answerLabel')}
                                  placeholder={t(
                                    'organisation:booking.question.title.answerPlaceholder',
                                  )}
                                  value={title}
                                  onChange={handleChange}
                                  isError={
                                    get(
                                      touched,
                                      `questions.${questionIdx}.answers.${answerIdx}.title`,
                                    ) &&
                                    !!get(
                                      errors,
                                      `questions.${questionIdx}.answers.${answerIdx}.title`,
                                    )
                                  }
                                  onBlur={() =>
                                    setFieldTouched(
                                      `questions.${questionIdx}.answers.${answerIdx}.title`,
                                    )
                                  }
                                />
                                <div className={'columns'}>
                                  <div className={'column is-6'}>
                                    <SelectField
                                      floatDropdown
                                      name={`questions.${questionIdx}.answers.${answerIdx}.pricingType`}
                                      label={t('organisation:booking.question.pricingType.label')}
                                      value={pricingType}
                                      options={
                                        value.selectType !== 'multiple'
                                          ? [
                                              {
                                                value: 'add',
                                                label: t(
                                                  'organisation:booking.question.pricingType.options.add',
                                                ),
                                              },
                                              {
                                                value: 'discount',
                                                label: t(
                                                  'organisation:booking.question.pricingType.options.discount',
                                                ),
                                              },
                                              {
                                                value: 'custom price',
                                                label: t(
                                                  'organisation:booking.question.pricingType.options.customPrice',
                                                ),
                                              },
                                            ]
                                          : [
                                              {
                                                value: 'add',
                                                label: t(
                                                  'organisation:booking.question.pricingType.options.add',
                                                ),
                                              },
                                              {
                                                value: 'discount',
                                                label: t(
                                                  'organisation:booking.question.pricingType.options.discount',
                                                ),
                                              },
                                            ]
                                      }
                                      onChange={handleChange}
                                    />
                                  </div>
                                  <div className={'column is-6'}>
                                    <TextField
                                      type={pricingType === 'custom price' ? 'text' : 'number'}
                                      name={`questions.${questionIdx}.answers.${answerIdx}.pricingValue`}
                                      label={t('organisation:booking.question.price.label')}
                                      placeholder={t(
                                        'organisation:booking.question.price.placeholder',
                                      )}
                                      value={pricingValue}
                                      onChange={handleChange}
                                    />
                                  </div>
                                </div>
                              </div>
                            )
                          }
                        })}
                        {answersType === 'button' && (
                          <>
                            <Divider className="ctp-shop-item__answer-divider" />
                            <button
                              className="button button-primary is-small"
                              onClick={() => {
                                arrayHelpers.push({
                                  title: '',
                                  type: answersType,
                                  pricingType: 'add',
                                  pricingValue: 0,
                                  placeholder: '',
                                })
                              }}
                            >
                              {t('organisation:booking.question.addAnswer')}
                            </button>
                          </>
                        )}
                      </React.Fragment>
                    )
                  }}
                </FieldArray>
              </div>
            </Accordion.Body>
          </Accordion>
        )
      }}
    </Field>
  )
}

ServiceQuestionItem.propTypes = {
  index: PropTypes.number.isRequired,
  question: PropTypes.object,
  deleteServiceQuestion: PropTypes.func.isRequired,
  deleteAnswer: PropTypes.func.isRequired,
  accordionCollapsed: PropTypes.bool,
  accordionToggle: PropTypes.func,
  questionArrayHelpers: PropTypes.object,
  reorderQuestion: PropTypes.func,
}

export default withAccordionControl(ServiceQuestionItem)
